import useTodayVwap from "../hooks/useTodayVwap";
import useAnchoredVwap from "../hooks/useAnchoredVwap";

/**
 * "VWAP Status" pill rendered in the dashboard header next to the price.
 *
 * mode:
 *   - "intraday" (default) — today's running session VWAP from the
 *     /api/stock/intraday-live feed. Resets every market open. Standard
 *     day-trading reference.
 *   - "anchored"           — anchored VWAP for the chart `timeframe` (1d/5d/
 *     1mo/3mo/6mo/1y). Accumulates from the first bar of the period and
 *     reflects the volume-weighted average over the selected window.
 *
 * date — optional YYYY-MM-DD historical date (intraday mode only — passes
 * through to the hook so the badge can follow the 60-Day History selector).
 */
export default function VwapStatusBadge({
  ticker,
  currentPrice,
  date,
  mode = "intraday",
  timeframe = "1d",
}) {
  const intradayState = useTodayVwap(ticker, date);
  const anchoredState = useAnchoredVwap(ticker, timeframe);

  const isAnchored = mode === "anchored";
  const state = isAnchored ? anchoredState : intradayState;

  if (!state.hasData || state.latest == null || !currentPrice) return null;

  const delta = currentPrice - state.latest;
  const deltaPct = (delta / state.latest) * 100;
  const above = delta >= 0;

  const tone = above
    ? "bg-emerald-950/40 border-emerald-700/60 text-emerald-300"
    : "bg-red-950/40 border-red-700/60 text-red-300";
  const dot = above ? "🟢" : "🔴";
  const label = above ? "above VWAP" : "below VWAP";

  // Distinct testid per mode so we can target each badge in tests/users
  const testid = isAnchored ? "vwap-status-badge-anchored" : "vwap-status-badge";

  // Visual prefix differentiates anchored from intraday at a glance
  const prefix = isAnchored ? `Anchored · ${timeframe.toUpperCase()}` : "Session";

  const tooltip = isAnchored
    ? `Anchored VWAP $${state.latest.toFixed(2)} · period ${timeframe.toUpperCase()} · open $${state.openPrice?.toFixed?.(2) ?? "—"}`
    : `Session VWAP $${state.latest.toFixed(2)} · open $${state.openPrice?.toFixed?.(2) ?? "—"}`;

  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-semibold ${tone}`}
      data-testid={testid}
      title={tooltip}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-70 hidden lg:inline">
        {prefix}
      </span>
      <span>{dot}</span>
      <span>
        {above ? "+" : ""}
        {deltaPct.toFixed(2)}%
      </span>
      <span className="text-[10px] opacity-80 hidden sm:inline">{label}</span>
      <span className="text-[10px] opacity-60 hidden md:inline">
        · ${state.latest.toFixed(2)}
      </span>
    </div>
  );
}
